import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Crown, Search, PenLine, Palette, TrendingUp, Megaphone, Globe, Instagram, Mail, BarChart2, HeartHandshake, Package, Star, Zap, DollarSign, Handshake, Scale } from 'lucide-react';

const TEAM = [
  { id: 1, name: "Coordinador Central", role: "Director de Operaciones", status: "core", description: "El jefe del equipo. El único que habla contigo. Toma todas las decisiones estratégicas, genera el informe diario a las 8am y coordina a todos los agentes.", color: "bg-amber-100 text-amber-800 ring-amber-200", icon: Crown },
  { id: 2, name: "Analista de Mercado", role: "Inteligencia Competitiva", status: "core", description: "Monitoriza la competencia y el mercado en tiempo real con web search. Detecta tendencias, oportunidades y amenazas.", color: "bg-blue-100 text-blue-800 ring-blue-200", icon: Search },
  { id: 3, name: "Copywriter", role: "Copy y Contenido", status: "core", description: "Genera copies para web, emails, anuncios y guiones de Reels y TikToks completos listos para usar.", color: "bg-pink-100 text-pink-800 ring-pink-200", icon: PenLine },
  { id: 4, name: "Diseñador Creativo", role: "Dirección Visual", status: "core", description: "Dirección visual, creatividades para ads y redes, coherencia de marca. Genera briefings visuales.", color: "bg-violet-100 text-violet-800 ring-violet-200", icon: Palette },
  { id: 5, name: "Especialista en Conversión", role: "CRO y Ventas", status: "core", description: "Optimiza el embudo completo: landing, checkout, precio, upsells, ventas perdidas. Usa GA4 y Clarity.", color: "bg-yellow-100 text-yellow-800 ring-yellow-200", icon: TrendingUp },
  { id: 6, name: "Especialista en Ads", role: "Publicidad Digital", status: "core", description: "Analiza Meta Ads y TikTok Ads mediante capturas. Propone creatividades, copies y segmentaciones.", color: "bg-orange-100 text-orange-800 ring-orange-200", icon: Megaphone },
  { id: 7, name: "SEO", role: "Posicionamiento Orgánico", status: "core", description: "Keywords, posicionamiento, artículos de blog que publica directamente en la web. Web search obligatorio.", color: "bg-emerald-100 text-emerald-800 ring-emerald-200", icon: Globe },
  { id: 8, name: "Community Manager", role: "Redes Sociales", status: "core", description: "Instagram, TikTok, Facebook. Genera calendarios, guiones completos, hashtags y analiza qué funciona.", color: "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200", icon: Instagram },
  { id: 9, name: "Email Marketing", role: "Brevo y Retención", status: "core", description: "Gestiona la lista de Brevo. Genera secuencias, newsletters y flujos de captación listos para enviar.", color: "bg-purple-100 text-purple-800 ring-purple-200", icon: Mail },
  { id: 10, name: "Analista de Datos", role: "Métricas y KPIs", status: "core", description: "Monitoriza todas las métricas. Detecta anomalías y patrones entre conectores. Fuente de cada venta.", color: "bg-indigo-100 text-indigo-800 ring-indigo-200", icon: BarChart2 },
  { id: 11, name: "Customer Success", role: "Clientes y Fidelización", status: "core", description: "Gmail y WhatsApp de clientes. Upsell, fidelización, reseñas en vídeo, preguntas pre-compra.", color: "bg-teal-100 text-teal-800 ring-teal-200", icon: HeartHandshake },
  { id: 12, name: "Product Manager", role: "Producto y Desarrollo", status: "core", description: "Mejoras del producto actual. Estrategia y desarrollo del Producto 2. Feedback de clientes.", color: "bg-cyan-100 text-cyan-800 ring-cyan-200", icon: Package },
  { id: 13, name: "Reputación y Testimonios", role: "Marca y Confianza", status: "core", description: "Reseñas, menciones, casos de éxito, comentarios negativos. Gestiona el proceso de reseñas en vídeo.", color: "bg-green-100 text-green-800 ring-green-200", icon: Star },
  { id: 14, name: "Growth Hacker", role: "Crecimiento Acelerado", status: "soon", description: "Estrategias de viralidad, nuevos canales de adquisición y experimentos de crecimiento rápido.", color: "bg-slate-100 text-slate-800 ring-slate-200", icon: Zap },
  { id: 15, name: "CFO / Finanzas", role: "Finanzas y Márgenes", status: "soon", description: "Márgenes, cashflow, pricing y punto de equilibrio. Camino a 10.000€ netos al mes.", color: "bg-slate-100 text-slate-800 ring-slate-200", icon: DollarSign },
  { id: 16, name: "Partnerships", role: "Colaboraciones", status: "soon", description: "Colaboraciones, afiliados y co-marketing. Identifica socios estratégicos.", color: "bg-slate-100 text-slate-800 ring-slate-200", icon: Handshake },
  { id: 17, name: "Legal", role: "Asesoría Legal", status: "soon", description: "RGPD, términos, política de privacidad. Normativa española/europea y LLC de Estados Unidos.", color: "bg-slate-100 text-slate-800 ring-slate-200", icon: Scale },
];

const TeamPage = () => {
  const core = TEAM.filter(a => a.status === 'core');
  const soon = TEAM.filter(a => a.status === 'soon');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Tu Equipo de Élite
          </CardTitle>
          <CardDescription>
            {core.length} agentes activos 24/7 + {soon.length} especializados que se activan cuando se necesitan.
            Meta común: llevar tu negocio a 10.000€ netos al mes.
          </CardDescription>
        </CardHeader>
      </Card>

      <div>
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
          Agentes Activos 24/7 — {core.length}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {core.map(agent => {
            const Icon = agent.icon;
            return (
              <Card key={agent.id}>
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ring-2 shrink-0 ${agent.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{agent.name}</span>
                        <Badge className="bg-green-500 text-white text-xs">ACTIVO</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">{agent.role}</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{agent.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-amber-500 inline-block"></span>
          Agentes Especializados — Se activan cuando se necesitan
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {soon.map(agent => {
            const Icon = agent.icon;
            return (
              <Card key={agent.id} className="opacity-80">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ring-2 shrink-0 ${agent.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{agent.name}</span>
                        <Badge className="bg-amber-500 text-white text-xs">ESPECIALIZADO</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">{agent.role}</p>
                      <p className="text-xs text-slate-600 leading-relaxed">{agent.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamPage;
